'use client'

import React, { useEffect, useState, useRef } from 'react';
import styles from './SearchDemo.module.css';
import {
  useSearchContext,
  SearchInput,
  SearchResults,
  SearchProvider,
  BucketFilterPanel,
  ValueFilterPanel,
  RangeFilterPanel,
  SortByPanel,
  SearchResultRow
} from '@indxsearch/intrface';
import type { SearchResultMeta } from '@indxsearch/intrface';
import { Base, Button, Popover, Chip } from '@indxsearch/systm';
import { Sliders_horizontal, Indx } from '@indxsearch/pixl';

export const fields = ['id', 'title', 'original_title', 'description', 'actors', 'release_year', 'vote_average', 'vote_count_tier', 'genres', 'release_year', 'decade', 'popularity', 'language'];

export const filters = (
  <>
    <ValueFilterPanel label="Genres" layout="grid" field="genres" preserveBlankFacetState={true} control="button" limit={10} />
    <ValueFilterPanel label="Actors" field="actors" control="checkbox" startCollapsed={true} limit={5} />
    <RangeFilterPanel label="Release Year" field="release_year" control="slider" expectedMin={1900} expectedMax={2025} showHistogram={true} resolution={5} />
    <ValueFilterPanel label="Decade" field="decade" control="checkbox" startCollapsed={true} preserveBlankFacetState={true} />
    <SortByPanel control="select" startCollapsed={true} />
    <BucketFilterPanel
      label="Rating"
      field="vote_average"
      control="checkbox"
      buckets={[
        { label: '8 and up', min: 8 },
        { label: '7 - 8', min: 7, max: 7.9999 },
        { label: '6 - 7', min: 6, max: 6.9999 },
        { label: 'Below 6', max: 5.9999 },
      ]}
      showEmpty={true}
      startCollapsed={true}
    />
    <ValueFilterPanel label="Vote Count" field="vote_count_tier" control="checkbox" startCollapsed={true} />
    <ValueFilterPanel label="Language" field="language" control="checkbox" startCollapsed={true} />
  </>
);

// Each film's poster, by its TMDB id: small WebP files in public/posters (data/tmdb/posters.mts).
// Keyed by film, because a reused img keeps showing the previous film's poster until the next one
// has loaded; a film without a poster keeps the empty frame rather than a broken image.
const posterSrc = (id: unknown) => `/posters/${id}.webp`;

// The film as a result. `description` shows the film's description under its title: the Search
// or ask version (SearchAskDemo) searches a tmdb23k where it is searchable.
export const renderFilm = ({ description = false } = {}) => (item: any, meta: SearchResultMeta) => (
  <div className={styles.film}>
    <img
      key={meta.result.documentKey}
      className={styles.poster}
      src={posterSrc(item.id ?? meta.result.documentKey)}
      alt=""
      loading="lazy"
      onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
    />
    <div className={styles.filmBody}>
    <SearchResultRow variant="title">
      {item.title}
      {item.original_title && item.original_title !== item.title && (
        <span className={styles.secondaryText}>({item.original_title})</span>
      )}
      {item.release_year && (
        <Chip>{item.release_year}</Chip>
      )}
      {typeof item.vote_average === 'number' && (
        <Chip>{item.vote_average.toFixed(1)}</Chip>
      )}
    </SearchResultRow>

    {/* Hidden where the description is stored but not searchable (the cloud tmdb23k). */}
    {description && item.description && (
      <p className={styles.descriptionText}>{item.description}</p>
    )}

    {Array.isArray(item.genres) && item.genres.length > 0 && (
      <SearchResultRow>
        Genres:{' '}
        {item.genres.map((genre: string, idx: number) => (
          <Chip key={`${genre}-${idx}`}>{genre}</Chip>
        ))}
      </SearchResultRow>
    )}

    {Array.isArray(item.actors) && item.actors.length > 0 && (
      <SearchResultRow>
        Cast:{' '}
        {item.actors.slice(0, 5).map((actor: string, idx: number) => (
          <Chip key={`${actor}-${idx}`}>{actor}</Chip>
        ))}
        {item.actors.length > 5 && <Chip>+{item.actors.length - 5} more</Chip>}
      </SearchResultRow>
    )}

    {typeof item.vote_count_tier === 'number' && (
      <div className={styles.voteDisplay}>
        {item.vote_count_tier.toLocaleString()} votes
      </div>
    )}
    </div>
  </div>
);
const renderResult = renderFilm();

interface SearchLayoutProps {
  onSearchFocus?: () => void
  onSearchBlur?: () => void
  onQueryChange?: (query: string) => void
}

function SearchLayout({ onSearchFocus, onSearchBlur, onQueryChange }: SearchLayoutProps = {}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showFilterButton, setShowFilterButton] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { state, setSort, setSearchSettings } = useSearchContext();
  const { filters: activeFilters, rangeFilters } = state;

  const hasFilters =
    Object.keys(activeFilters).length > 0 ||
    Object.keys(rangeFilters).length > 0;

  // Set initial sort order and placeholder text
  useEffect(() => {
    setSort('popularity', false); // Sort by popularity, descending
    setSearchSettings({
      placeholderText: 'Search for a movie',
      coverageDepth: 2000
    });
  }, [setSort, setSearchSettings]);

  // Call onQueryChange when search query changes
  useEffect(() => {
    if (onQueryChange && state.query !== undefined) {
      onQueryChange(state.query);
    }
  }, [state.query, onQueryChange]);

  // RESIZE HANDLER
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        const isNarrow = width <= 800;
        setShowFilterButton(isNarrow);

        if (!isNarrow && filtersOpen) {
          setFiltersOpen(false);
        }
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [filtersOpen]);

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <Base className={styles.component}>
        <div className={styles.mainContent}>
          <div className={styles.header}>
            <SearchInput
              showFocus={false}
              className={styles.searchInput}
              onFocus={onSearchFocus}
              onBlur={onSearchBlur}
            />
            <div className={styles.meta}>
              {showFilterButton && (
                <div style={{ marginRight: '10px' }}>
                  <Popover
                    trigger={
                      <Button
                        variant={hasFilters ? 'primary' : 'secondary'}
                        iconRight={<Sliders_horizontal />}
                        size='micro'
                      >
                        Filters
                      </Button>
                    }
                    open={filtersOpen}
                    onOpenChange={setFiltersOpen}
                    align="end"
                    sideOffset={5}
                    className={styles.popoverContent}
                  >
                    <div className={styles.scrollFilters}>
                      {filters}
                    </div>
                  </Popover>
                </div>
              )}
              <span className={styles.logo}>
                <Indx size={28} color="var(--lv4)" />
              </span>
            </div>
          </div>
          <div className={styles.body}>
            <SearchResults fields={fields} resultsPerPage={6}>
              {renderResult}
            </SearchResults>
          </div>
        </div>
        <div className={styles.filtersColumn}>
          {filters}
        </div>
      </Base>
      {/* TMDB's terms ask for this wherever their data or images are shown. Below the whole
          search component, so it stays put while the results change. */}
      <p className={styles.credit}>
        Film data and posters from <a href="https://www.themoviedb.org" target="_blank" rel="noopener noreferrer">TMDB</a>.
        This product uses TMDB data but is not endorsed or certified by TMDB.
      </p>
    </div>
  );
}

interface SearchDemoProps {
  onSearchFocus?: () => void
  onSearchBlur?: () => void
  onQueryChange?: (query: string) => void
}

export default function SearchDemo({ onSearchFocus, onSearchBlur, onQueryChange }: SearchDemoProps = {}) {
  const url = process.env.NEXT_PUBLIC_INDX_URL!;
  const token = process.env.NEXT_PUBLIC_INDX_TOKEN!;
  const dataset = process.env.NEXT_PUBLIC_INDX_DATASET!;

  return (
    <SearchProvider
      url={url}
      team="indx"
      dataset={dataset}
      preAuthenticatedToken={token}
      allowEmptySearch={true}
      enableFacets={true}
      maxResults={30}
      facetDebounceDelayMillis={200}
      enableDebugLogs={false}
    >
      <SearchLayout
        onSearchFocus={onSearchFocus}
        onSearchBlur={onSearchBlur}
        onQueryChange={onQueryChange}
      />
    </SearchProvider>
  );
}
